import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import socket, { sessionId, storage } from '../socket';
import {
  clearChallenge, setChallengeDeclined, setPendingChallenge,
  setChallengeError, resetGame,
} from '../store/slices/lobbySlice';
import { logout } from '../store/slices/userSlice';
import { resetBS } from '../store/slices/battleshipSlice';

export default function Lobby() {
  const dispatch = useDispatch();
  const myName = useSelector((s) => s.user.name);
  const players = useSelector((s) => s.lobby.players);
  const incoming = useSelector((s) => s.lobby.incomingChallenge);
  const declined = useSelector((s) => s.lobby.challengeDeclined);
  const pendingChallenge = useSelector((s) => s.lobby.pendingChallenge);
  const challengeError = useSelector((s) => s.lobby.challengeError);
  const [selectedGame, setSelectedGame] = useState('battleship');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const others = players.filter((p) => p.id !== sessionId);

  const handleLogout = () => {
    socket.emit('player:logout');
    // Keep the sessionId — it identifies this device, not the login
    storage.removeItem('gamePortal_name');
    dispatch(resetBS());
    dispatch(resetGame());
    dispatch(logout());
    socket.disconnect();
  };

  const challenge = (targetId) => {
    if (!socket.connected) return;
    dispatch(setPendingChallenge(targetId));
    socket.emit('challenge', { targetId, gameType: selectedGame });
  };

  const cancelChallenge = () => {
    socket.emit('challenge:cancel', { targetId: pendingChallenge });
    dispatch(setPendingChallenge(null));
  };

  const accept = () => {
    socket.emit('challenge:accept', {
      challengerId: incoming.from,
      gameType: incoming.gameType,
    });
    dispatch(clearChallenge());
  };

  const decline = () => {
    socket.emit('challenge:decline', { challengerId: incoming.from });
    dispatch(clearChallenge());
  };

  return (
    <div className="lobby-container">
      <header className="lobby-header">
        <h1>🎮 Game Portal</h1>
        <div className="header-right">
          <span className="user-badge">👤 {myName}</span>
          <button className="btn btn-small btn-logout" onClick={() => setShowLogoutConfirm(true)}>Logout</button>
        </div>
      </header>

      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>🚪 Logout</h3>
            <p>Are you sure? All your gaming progress will be <strong>erased irreversibly</strong>.</p>
            <div className="modal-actions">
              <button className="btn btn-danger" onClick={handleLogout}>Yes, Logout</button>
              <button className="btn btn-primary" onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Game selector */}
      <div className="game-selector">
        <label className="game-option">
          <input
            type="radio" name="game" value="tictactoe"
            checked={selectedGame === 'tictactoe'}
            onChange={() => setSelectedGame('tictactoe')}
          />
          <span className="game-label">❌⭕ Tic-Tac-Toe</span>
        </label>
        <label className="game-option">
          <input
            type="radio" name="game" value="battleship"
            checked={selectedGame === 'battleship'}
            onChange={() => setSelectedGame('battleship')}
          />
          <span className="game-label">🚢 Battleship</span>
        </label>
      </div>

      {/* Incoming challenge modal */}
      {incoming && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>⚔️ Challenge!</h3>
            <p>
              <strong>{incoming.name}</strong> wants to play{' '}
              {incoming.gameType === 'tictactoe' ? 'Tic-Tac-Toe' : 'Battleship'}
            </p>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={accept}>Accept</button>
              <button className="btn btn-danger" onClick={decline}>Decline</button>
            </div>
          </div>
        </div>
      )}

      {declined && (
        <div className="toast toast-warn" onClick={() => dispatch(setChallengeDeclined(false))}>
          Challenge was declined. Click to dismiss.
        </div>
      )}

      {challengeError && (
        <div className="toast toast-warn" onClick={() => dispatch(setChallengeError(null))}>
          {challengeError} Click to dismiss.
        </div>
      )}

      {/* Player list */}
      <div className="player-list">
        <h2>Online Players <span className="player-count">({players.length})</span></h2>
        {others.length === 0 ? (
          <p className="empty-msg">Waiting for other players to join...</p>
        ) : (
          others.map((p) => (
            <div key={p.id} className="player-card">
              <span className="player-name">
                {p.name}
                <span className={`status-dot ${p.status}`} />
              </span>
              {pendingChallenge === p.id ? (
                <span className="pending-badge">
                  ⏳ Waiting...{' '}
                  <button className="btn btn-small btn-leave" onClick={cancelChallenge}>Cancel</button>
                </span>
              ) : (
                <button
                  className="btn btn-small"
                  disabled={p.status !== 'idle' || !!pendingChallenge}
                  onClick={() => challenge(p.id)}
                >
                  {p.status === 'idle' ? 'Challenge' : 'In Game'}
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
